import ArrowDownIcon from "@/assets/icons/arrow-down.svg";
import classNames from "classnames";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import styles from "./index.module.css";

export interface ExpandableContainerProps {
  /** 子组件内容 */
  children: ReactNode;
  /** 最大高度（折叠时），单位 px，默认 550 */
  maxHeight?: number;
  /** 渐变遮罩的高度，单位 px，默认 150 */
  maskHeight?: number;
  /** 展开按钮文本，默认 "Expand" */
  expandText?: string;
  /** 折叠按钮文本，默认 "Collapse" */
  collapseText?: string;
  /** 自定义按钮类名 */
  buttonClassName?: string;
  /** 自定义容器类名 */
  containerClassName?: string;
  /** 是否默认展开，默认 false */
  defaultExpanded?: boolean;
  /** 控制展开状态（受控模式） */
  expanded?: boolean;
  /** 展开状态变化回调 */
  onExpandChange?: (expanded: boolean) => void;
}

export const ExpandableContainer: React.FC<ExpandableContainerProps> = ({
  children,
  maxHeight = 550,
  maskHeight = 150,
  expandText = "Expand",
  collapseText = "Collapse",
  buttonClassName,
  containerClassName,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandChange,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const [showToggleButton, setShowToggleButton] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined
  );

  // 判断是受控还是非受控模式
  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  useEffect(() => {
    if (contentRef.current) {
      // 临时移除 maxHeight 限制来获取真实的完整高度
      const element = contentRef.current;
      const originalMaxHeight = element.style.maxHeight;
      element.style.maxHeight = "none";
      const height = element.scrollHeight;
      element.style.maxHeight = originalMaxHeight;

      setContentHeight(height);
      setShowToggleButton(height > maxHeight);
    }
  }, [children, maxHeight]);

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    if (!isControlled) {
      setInternalExpanded(newExpanded);
    }
    onExpandChange?.(newExpanded);
  };

  return (
    <div className={classNames("relative", containerClassName)}>
      <div
        ref={contentRef}
        className={classNames(
          "overflow-hidden transition-all duration-500 ease-in-out",
          !isExpanded && styles.collapsed
        )}
        style={{
          maxHeight: isExpanded
            ? contentHeight
              ? `${contentHeight}px`
              : "none"
            : `${maxHeight}px`,
          ["--mask-height" as string]: `${maskHeight}px`,
        }}
      >
        {children}
      </div>
      {showToggleButton && (
        <button
          onClick={handleToggle}
          className={classNames(
            "absolute w-full text-text-primary rounded-full h-11 text-xs flex items-center justify-center gap-2 font-medium transition-all z-10 cursor-pointer",
            isExpanded ? "" : "bottom-2",
            buttonClassName
          )}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? collapseText : expandText}
        >
          {isExpanded ? collapseText : expandText}
          <ArrowDownIcon
            className={`w-4 h-4 transition-transform duration-300 ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
      )}
    </div>
  );
};

ExpandableContainer.displayName = "ExpandableContainer";
